import { useTranslation } from 'react-i18next';
import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  SwipeableDrawer,
  Typography,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CloseIcon from '@mui/icons-material/Close';
import DrawerItem from './DrawerItem';
import { CategoryFiltersInterface } from '../CategoryFiltersUtils';

interface DrawerListProps extends CategoryFiltersInterface {
  closeRef?: React.RefObject<HTMLButtonElement | null>;
  onToggleDrawer: (open: boolean) => void;
}

function DrawerList({
  activeCategory,
  categories = [],
  loading,
  onSelectCategory = () => {},
  closeRef,
  onToggleDrawer,
}: Readonly<DrawerListProps>) {
  const { t } = useTranslation();

  const handleToggleDrawer = (open: boolean) => () => {
    onToggleDrawer(open);
  };

  function handleCategoryClick(category?: string) {
    onSelectCategory(category);

    onToggleDrawer(false);
  }

  return (
    <>
      <Stack
        direction='row'
        data-testid='category-filter-drawer-content'
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='h2'
          sx={{
            paddingLeft: 2,
            paddingY: 2,
          }}
        >
          {t('categoryList.selectCategory')}
        </Typography>
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
          }}
        >
          <Button
            onClick={handleToggleDrawer(false)}
            size='medium'
            sx={{ color: 'text.primary' }}
            endIcon={<CloseIcon />}
            data-testid='close-drawer-button'
            ref={closeRef}
          >
            {t('categoryList.closeList')}
          </Button>
        </Stack>
      </Stack>
      <Divider />
      <Box sx={{ overflowY: 'scroll' }}>
        <DrawerItem
          active={activeCategory === undefined}
          onClickCallback={() => handleCategoryClick()}
        >
          {t('categoryList.allCategories')}
        </DrawerItem>
        <Divider />
        {categories.map((category: string) => {
          return (
            <Fragment key={category + '-fragment'}>
              <DrawerItem
                active={activeCategory === category}
                onClickCallback={() => handleCategoryClick(category)}
              >
                {category}
              </DrawerItem>
              <Divider />
            </Fragment>
          );
        })}
        {loading && <CircularProgress />}
      </Box>
    </>
  );
}

export default function CategoryFiltersDrawer({
  activeCategory,
  categories,
  loading,
  onSelectCategory,
}: Readonly<CategoryFiltersInterface>) {
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const toggleDrawer = (open: boolean) => {
    setOpenDrawer(open);
  };

  useEffect(() => {
    if (openDrawer && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    if (!openDrawer && openButtonRef.current) {
      openButtonRef.current.focus();
    }
  }, [openDrawer]);

  return (
    <>
      <Button
        variant='text'
        sx={{ color: 'text.secondary' }}
        onClick={() => toggleDrawer(true)}
        aria-haspopup='true'
        aria-expanded={openDrawer}
        ref={openButtonRef}
        id='category-filter-drawer-open'
      >
        {t('categoryList.title')}
        <UnfoldMoreIcon fontSize='inherit' sx={{ marginLeft: 1 }} />
      </Button>
      <SwipeableDrawer
        anchor='bottom'
        open={openDrawer}
        onClose={() => toggleDrawer(false)}
        onOpen={() => toggleDrawer(true)}
        disableSwipeToOpen
        disableDiscovery
        sx={{ zIndex: 1302 }} // This is to make the backdrop appear on top the header
        slotProps={{
          paper: {
            sx: { maxHeight: '80%' },
          },
        }}
        aria-labelledby='category-filter-drawer-open'
      >
        <DrawerList
          activeCategory={activeCategory}
          categories={categories}
          loading={loading}
          closeRef={closeButtonRef}
          onToggleDrawer={(open) => toggleDrawer(open)}
          onSelectCategory={onSelectCategory}
        />
      </SwipeableDrawer>
    </>
  );
}
